import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTemplateStore } from "../zustand/templateStore";
import CreditCards from "./creditCards";

const Accordion = ({ children, label, image, name, overflow, maxImgWidth, imageHeight }) => {
  const { paymentMethod, handlePaymentMethodChange } = useTemplateStore();
  const [expanded, setExpanded] = useState(false);
  const [overflowSetting, setOverflowSetting] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const contentRef = useRef(null);
  const innerContentRef = useRef(null);
  const [currentMaxHeight, setCurrentMaxHeight] = useState(0);

  const cardButtonColor = window?.post_template?.checkout_v4_products?.checkout_v4_products_checkButtonCcColor;
  const showBorderCC = window?.post_template?.checkout_v4_products?.checkout_v4_products_borderCC;
  const headingBgColor = window?.post_template?.checkout_v4_products?.checkout_v4_products_paymentHeadingBgColor;

  document.documentElement.style.setProperty("--payment-heading-bg-color", headingBgColor);
  document.documentElement.style.setProperty("--checkbutton-cc-color", cardButtonColor);

  useEffect(() => {
    let timer;
    if (overflow) {
      timer = setTimeout(() => {
        setOverflowSetting(true);
      }, 600);
    } else {
      setOverflowSetting(false);
    }
    return () => clearTimeout(timer);
  }, [overflow]);

  useEffect(() => {
    setIsInitialRender(false);
  }, []);

  useEffect(() => {
    setExpanded(name === paymentMethod);
  }, [paymentMethod, name]);

  const updateHeight = useCallback(() => {
    if (expanded && innerContentRef.current && contentRef.current) {
      const originalWrapperStyle = {
        overflow: contentRef.current.style.overflow,
        maxHeight: contentRef.current.style.maxHeight,
        visibility: contentRef.current.style.visibility,
      };

      contentRef.current.style.visibility = "hidden";
      contentRef.current.style.overflow = "visible";
      contentRef.current.style.maxHeight = "none";

      const _ = innerContentRef.current.offsetHeight;

      setCurrentMaxHeight(innerContentRef.current.scrollHeight + 64);

      contentRef.current.style.visibility = originalWrapperStyle.visibility;
      contentRef.current.style.overflow = originalWrapperStyle.overflow;
      contentRef.current.style.maxHeight = originalWrapperStyle.maxHeight;
    } else {
      setCurrentMaxHeight(0);
    }
  }, [expanded]);

  useEffect(() => {
    const element = innerContentRef.current;
    if (!element) return;

    if (expanded) {
      requestAnimationFrame(updateHeight);
    } else {
      setCurrentMaxHeight(0);
    }

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateHeight);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
      resizeObserver.disconnect();
    };
  }, [expanded, updateHeight]);

  const handleAccordionClick = (e) => {
    e.preventDefault();
    handlePaymentMethodChange(name);
  };

  const headingClass = showBorderCC
    ? expanded
      ? "accordionContainer__heading--border"
      : "accordionContainer__heading--border accordionContainer__heading--unchecked"
    : expanded
      ? "accordionContainer__heading"
      : "accordionContainer__heading accordionContainer__heading--unchecked";

  const wrapperStyle = {
    transition: isInitialRender && expanded ? "none" : undefined,
    overflow: overflowSetting ? "visible" : "hidden",
    maxHeight: `${currentMaxHeight}px`,
  };

  return (
    <>
      <div onClick={handleAccordionClick} expanded={expanded.toString()} className={headingClass}>
        <button className="accordionContainer__button" type="button"></button>
        {label && (
          <span data-testid="credit-card-label" className="cardRadio__labelText">
            {" "}
            {label}{" "}
          </span>
        )}
        {name === "card" ? (
          <CreditCards />
        ) : (
          <div className="accordionContainer__paypal">
            <span data-testid="paypal-label">PayPal</span>
            <img
              width="85"
              height="24"
              className="cardRadio__image"
              src={image}
              alt="Payments"
              style={{
                maxWidth: `${maxImgWidth}px`,
                marginLeft: label ? "auto" : 0,
                height: imageHeight && `${imageHeight}px`,
              }}
            />
          </div>
        )}
      </div>

      <section className="accordionContainer__wrapper" style={wrapperStyle} aria-expanded={expanded} ref={contentRef}>
        <div
          ref={innerContentRef}
          className={
            name === "paypal"
              ? "accordionContainer__content accordionContainer__content--paypal"
              : "accordionContainer__content"
          }
        >
          {children}
        </div>
      </section>
    </>
  );
};

export default Accordion;
